/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { CreateForm, CreateFormField, PickerQuickCreateOptions } from '../data-picker.interface';
import { ContactInfoService } from '../../../components/contact-info/contact-info.component.service';
import { DataPickerService } from '../../../core/services/data_picker/data_picker.service';
import { CustomersService } from 'app/core/services/customers/customers.service';

@Component({
    selector: 'quick-create-property',
    templateUrl: './quick-create-property.component.html',
    styleUrls: ['./quick-create-property.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class QuickCreatePropertyComponent implements OnInit, OnChanges, OnDestroy {
    @Input() formReset: boolean = false;
    @Input() formSubmit: boolean = false;
    @Input() prefillValues: any = null;
    @Input() formVisibility: boolean = false;
    @Input() quickCreateOptions?: PickerQuickCreateOptions;
    @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
    @Output() formValidationEmitter: EventEmitter<boolean> = new EventEmitter();

    propertyForm: UntypedFormGroup = null;
    organizations: Observable<any[]> = null;
    organizationOptions: any[] = [];
    pagination: any = {
        loadingIndex: 1,
        totalPage: 1,
        keyword: ''
    };
    loading: boolean = false;
    orgParamObj: any = {
        'count': 10,
        'page': 1,
        'filter.keyword': '',
        'sort': 'ASC',
        'sort_by': 'organization_name'
    };
    isFormLoaded: boolean = false;

    createProperty: CreateForm[] = [{
        headerName: 'Primary Information',
        headerDisplayKey: 'text.primary_information',
        canShow: true,
        formFields: [{
            labelName: 'Property Name',
            labelDisplayKey: 'text.property_name',
            fieldId: 'name',
            fieldType: 'input',
            inputType: 'text',
            formControlName: 'name',
            placeHolder: 'Property Name',
            placeHolderDisplayKey: 'text.property_name',
            isMandatory: true,
            validationText: 'Property Name field is required',
            validationTextKey: 'create.property.name.validText'
        }, {
            labelName: 'Choose Organization',
            labelDisplayKey: 'choose.organization',
            fieldId: 'organization',
            fieldType: 'lazyautocomplete',
            inputType: 'text',
            formControlName: 'organization',
            placeHolder: 'Choose Organization',
            placeHolderDisplayKey: 'choose.organization',
            observable: this.organizations,
            oberservableKey: 'organization_name',
            oberservableValue: 'organization_uid',
            allowLoading: true,
            allowLazyLoading: true,
            valueSubject: new ReplaySubject<string | null>(),
            showIf: this.utilityService.hasModuleAccess('ORGANIZATION', false, 'from_sidebar'),
        }]
    }, {
        headerName: 'Property Address',
        headerDisplayKey: 'text.property_address',
        headerType: 'address',
        formGroupName: 'propertyAddress',
        canShow: true,
        formFields: [],
        contactFields: {
            contactFirstName: false,
            contactLastName: false,
            contactEmail: false,
            contactPhone: false,
            street: true,
            enableMapsAutoComplete: true,
            canTruncate: true,
            landmark: false,
            city: true,
            state: true,
            country: true,
            zipcode: true,
            latitude: false,
            longitude: false
        }
    }];
    formControls: any[] = [{
        formControlName: 'name',
        value: '',
        validators: [Validators.required]
    }, {
        formControlName: 'organization',
        value: '',
        validators: []
    }, {
        formControlName: 'propertyAddress',
        controls: []
    }];
    pickerAPIUrls: any;

    constructor(
        private dataCollectorService: DataCollectorService,
        private localisationService: LocalisationService,
        private utilityService: UtilityService,
        private pickerService: DataPickerService,
        private contactInfoService: ContactInfoService,
        private toast: HotToastService,
        private customerService: CustomersService,
        private eleRef: ElementRef) {
        this.propertyForm = this.pickerService.createFormControls(this.formControls);
    }
    ngOnDestroy(): void {
        this.customerService.selectedOrganizationPropertyPickerFilterValue.next(null);
    }

    ngOnInit(): void {
        this.localisationService.setDefaultLanguage();
        this.pickerAPIUrls = this.pickerService.pickerAPIUrls;
        if (this.formVisibility) {
            this.updateCreateFormVisibility();
        }
        this.customerService.selectedOrganizationPropertyPickerFilterValue.subscribe((res)=>{
            const organizationControl: AbstractControl = this.propertyForm?.get('organization');
            if (!this.utilityService.isEmptyOrUndefined(res)) {
                this.orgParamObj['filter.organization_uid'] = res.organization_uid;
                this.prefillValues = {...this.prefillValues,organization : res.organization_uid};
            } else {
                delete this.orgParamObj?.['filter.organization_uid'];
                delete this.prefillValues?.['organization'];
                organizationControl?.enable();
            }
          });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.formReset?.currentValue === true) {
            this.resetForm();
        }

        if (changes?.formSubmit?.firstChange === false && changes?.formSubmit?.currentValue === true) {
            this.submitQuickCreateForm();
        }

        if (changes?.formVisibility?.firstChange === false && changes?.formVisibility?.currentValue === true) {
            this.updateCreateFormVisibility();
        }
    }

    updateCreateFormVisibility(): void {
        if (!this.isFormLoaded) {
            this.isFormLoaded = true;
            this.openQuickCreateForm();
        } else {
            this.setOrganizationPrefillValue(this.organizationOptions);
        }
    }

    openQuickCreateForm(): void {
        this.updatePrefillValuesFilter();
        this.getData();
        if(this.quickCreateOptions?.disableOrganization){
            this.propertyForm?.get('organization')?.disable();
        }
        if(this.quickCreateOptions?.hideOrganization){
            this.createProperty.forEach((item) => {
                item.formFields.forEach((element) => {
                    if(element.formControlName === 'organization'){
                        element.showIf = false;
                    }
                });
            });
        }
    }

    resetForm(): void {
        this.propertyForm.reset();
    }

    updatePrefillValuesFilter(): void {
        if (this.prefillValues) {
            Object.keys(this.prefillValues).forEach((key: string) => {
                if (key === 'organization' && this.prefillValues[key]) {
                    this.orgParamObj['filter.organization_uid'] = this.prefillValues[key];
                }
            });
        }
    }

    getErrorMessageKey(field: CreateFormField): string {
        if (this.propertyForm.get(field.formControlName).hasError('required')) {
            return field.validationTextKey;
        }

        return '';
    }

    showError(formControlName: string): boolean {
        return this.propertyForm.get(formControlName)?.touched && !this.propertyForm.get(formControlName)?.valid;
    }

    getObservable(): Observable<any[]> {
        return this.organizations;
    }

    onSearch(valueSubject: ReplaySubject<string | null>): void {
        this.updateOrganizations(valueSubject);
    }

    onClear(): void {
        this.organizations = this.getOrganizations('');
    }

    updateOrganizations(valueSubject: ReplaySubject<string | null>): void {
        if (valueSubject) {
            this.organizations = valueSubject.pipe(
                distinctUntilChanged(),
                debounceTime(250),
                filter(value => (value !== null && value !== undefined)),
                switchMap(term => this.getOrganizations(term))
            );
        }
    }

    getOrganizations(input: string): Observable<any[]> {
        this.pagination.keyword = input ? input : '';
        this.orgParamObj['filter.keyword'] = this.pagination.keyword;
        this.orgParamObj['page'] = 1;
        const urlParams = this.utilityService.constructParam(this.orgParamObj);
        this.loading = true;
        return this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl, urlParams)
            .pipe(
                catchError(() => of([])),
                map((response) => {
                    this.pagination.loadingIndex = response.current_page;
                    this.pagination.totalPage = response.total_pages;
                    this.organizationOptions = response.data;
                    this.loading = false;
                    return response.data;
                }),
            );
    }

    onScrollToEnd(): void {
        if (this.pagination.loadingIndex < this.pagination.totalPage) {
            this.pagination.loadingIndex++;
            this.fetchMore();
        }
    }

    fetchMore(): void {
        this.loading = true;
        this.orgParamObj['filter.keyword'] = this.pagination.keyword;
        this.orgParamObj['page'] = this.pagination.loadingIndex;
        const urlParams = this.utilityService.constructParam(this.orgParamObj);

        this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl, urlParams).subscribe(
            (organizations: any) => {
                this.loading = false;
                this.organizationOptions = this.organizationOptions.concat(organizations.data);
                this.organizations = this.propertyForm.get('organization').valueChanges.pipe(
                    startWith(''),
                    map((value: any) => this.organizationOptions)
                );
            }, (_error: any) => {
                this.loading = false;
            });
    }

    getData(): void {
        this.orgParamObj['page'] = 1;
        const urlParams = this.utilityService.constructParam(this.orgParamObj);
        this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl, urlParams).subscribe(
            (organizations: any) => {
                this.pagination.loadingIndex = organizations.current_page;
                this.pagination.totalPage = organizations.total_pages;
                this.organizationOptions = organizations.data;
                this.organizations = this.propertyForm.get('organization').valueChanges.pipe(
                    startWith(''),
                    map((value: any) => this.organizationOptions)
                );
                this.setOrganizationPrefillValue(this.organizationOptions);
            }, (_error: any) => {
            });
    }

    setOrganizationPrefillValue(organizations: any[]): void {
        if (this.prefillValues?.['organization'] && organizations?.length) {
            setTimeout(() => {
                const organizationControl: AbstractControl = this.propertyForm?.get('organization');
                organizationControl?.setValue(this.prefillValues['organization']);
                organizationControl?.disable();
            }, 100);
        }
    }

    submitQuickCreateForm(): void {
        this.propertyForm.markAllAsTouched();
        if (this.propertyForm.valid) {
            this.createNewProperty();
        } else {
            this.pickerService.scrollToFirstInvalidControl(this.eleRef.nativeElement);
            this.formValidationEmitter.emit(false);
        }
    }

    createNewProperty(): void {
        const payLoad: any = this.getPropertyModel(this.propertyForm.getRawValue());
        this.dataCollectorService.postData(this.pickerAPIUrls.propertyUrl, payLoad)
            .subscribe(
                (response: any) => {
                    this.toast.success(response.message);
                    this.dataCollectorService.getData(this.pickerAPIUrls.propertyUrl + '/' + response.data.property_uid).subscribe(
                        (property: any) => {
                            this.eventEmitter.emit({ value: [property.data] });
                        }, (_error: any) => {
                        });
                }, (_error: any) => {
                    this.formValidationEmitter.emit(false);
                }
            );
    }

    getPropertyModel(formValue: any): any {
        return {
            property: {
                custom_fields: [],
                organization: this.utilityService.hasModuleAccess('ORGANIZATION', false, 'from_sidebar') ? (formValue.organization || null) : null,
                property_address: this.contactInfoService.getContactInfoAddressModel(formValue['propertyAddress']),
                property_customers: [],
                property_name: formValue.name
            }
        };
    }
}
